// Lightweight telemetry -- iteration 061
// Batched, fire-and-forget, non-blocking. Reuses data-page/data-action attributes from E2E instrumentation.

import type { TelemetryEvent, TelemetryBatch } from "@dugads/shared";
import { onLCP, onFID, onCLS, onTTFB } from "web-vitals";

declare const __APP_VERSION__: string;

const buffer: TelemetryEvent[] = [];
const SESSION_ID = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
const MAX_BUFFER = 50;

function isE2ETest(): boolean {
  return !!(window as unknown as { __E2E_TEST__?: boolean }).__E2E_TEST__;
}

/** Get current page name from data-page attribute */
function currentPage(): string {
  return document.querySelector("[data-page]")?.getAttribute("data-page") ?? "unknown";
}

/** Record a telemetry event */
export function track(event: Omit<TelemetryEvent, "timestamp">) {
  if (isE2ETest()) return;
  buffer.push({ ...event, timestamp: new Date().toISOString() });
  if (buffer.length >= MAX_BUFFER) flush();
}

/** Flush buffered events to the server */
function flush() {
  if (buffer.length === 0) return;
  const events = buffer.splice(0);
  const batch: TelemetryBatch = {
    events,
    client: { platform: "web", version: __APP_VERSION__ },
    session_id: SESSION_ID,
  };
  try {
    navigator.sendBeacon("/api/telemetry", new Blob([JSON.stringify(batch)], { type: "application/json" }));
  } catch {
    // Fire and forget -- drop silently
  }
}

/** Initialize telemetry: global click listener, flush timer, web-vitals */
export function initTelemetry() {
  if (isE2ETest()) return;

  // Global action click listener -- captures all [data-action] clicks
  document.addEventListener(
    "click",
    (e) => {
      const target = (e.target as HTMLElement).closest("[data-action]");
      if (!target) return;
      track({
        type: "action",
        page: currentPage(),
        action: target.getAttribute("data-action")!,
        value: target.getAttribute("data-value") ?? undefined,
      });
    },
    { capture: true },
  );

  // Flush on page hide (tab close, navigate away)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });

  // Flush every 30 seconds as backup
  setInterval(flush, 30_000);

  // Web Vitals -- performance metrics
  onLCP(({ value }) => track({ type: "perf", page: currentPage(), metric: "lcp", duration_ms: Math.round(value) }));
  onFID(({ value }) => track({ type: "perf", page: currentPage(), metric: "fid", duration_ms: Math.round(value) }));
  onCLS(({ value }) => track({ type: "perf", page: currentPage(), metric: "cls", value: String(value) }));
  onTTFB(({ value }) => track({ type: "perf", page: currentPage(), metric: "ttfb", duration_ms: Math.round(value) }));
}
